import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import { Company } from "../../types/types";
import { createCompanyAdmin, getCompaniesAdmin } from "../../api/administration";
import TextInput from "../../components/ui/TextInput";
import CompanyEditModal from "../../components/modals/CompanyEditModal";
import Icon from "../../components/common/Icon";

const Companies: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<string>("");

    const [newCompanyName, setNewCompanyName] = useState<string>("");
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getCompaniesAdmin(token);
            setCompanies(response);
        } catch (error) {
            console.error("Failed to fetch cities", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddCompany = async () => {
        try {
            if (!newCompanyName) return;

            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            await createCompanyAdmin({
                token,
                companyName: newCompanyName,
            });
            setNewCompanyName("");
            await fetchCompanies();
        } catch (error) {
            console.error("Failed to add city", error);
        }
    };

    const filteredCompanies = companies
        .filter((company) => company.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0));

    if (loading) return <div>Chargement...</div>;

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("companies")} subtitle={t("companiesSubTitle")} />
            </div>

            <section className={adminSurface.sectionFull}>
                <h2 className={adminSurface.sectionTitle}>{t("addNewCompany")}</h2>
                <p className={adminSurface.sectionHelp}>
                    {t("addNewCompanyHelp") ||
                        "L'entreprise pourra ensuite être associée à des expériences et des utilisateurs."}
                </p>
                <div className={`${adminSurface.addForm} ${adminSurface.card}`}>
                    <div className={adminSurface.addRow}>
                        <div className={`${adminSurface.addField} ${adminSurface.addFieldW320}`}>
                            <label className={adminSurface.addLabel} htmlFor="newCompanyName">
                                {t("enterCompanyName")}
                                <span className={adminSurface.requiredAsterisk}>*</span>
                            </label>
                            <TextInput
                                placeholder={t("enterCompanyName")}
                                onChange={(e) => setNewCompanyName(e.target.value)}
                                value={newCompanyName}
                                disabled={false}
                                customClassName="text-input-fluid"
                            />
                        </div>
                        <button
                            className={adminSurface.addButton}
                            onClick={handleAddCompany}
                            disabled={!newCompanyName}
                            type="button"
                        >
                            <Icon name="ic-plus" className="ic" size={16} />
                            {t("add")}
                        </button>
                    </div>
                </div>
            </section>

            <div className={adminSurface.tblBar}>
                <div className={adminSurface.searchWrap}>
                    <Icon name="ic-search" className={adminSurface.searchWrapIcon} size={18} />
                    <TextInput
                        customClassName={adminSurface.searchInput}
                        placeholder={t("searchCompany")}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                        disabled={false}
                    />
                </div>
            </div>

            <div className={adminSurface.tableCard}>
                <div className={adminSurface.tblScrollBody}>
                    <table className={adminSurface.table}>
                        <thead>
                            <tr>
                                <th>{t("companyName")}</th>
                                <th>{t("actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCompanies.map((company) => (
                                <tr key={company.id}>
                                    <td>{company.name}</td>
                                    <td>
                                        <div className="cities-table-actions">
                                            <button
                                                className={adminSurface.modifyButton}
                                                onClick={() => {
                                                    setSelectedCompany(company);
                                                    setShowEditModal(true);
                                                }}
                                            >
                                                {t("modify")}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={adminSurface.tblFoot}>
                    <span>
                        {filteredCompanies.length} {t("companies")?.toLowerCase?.() || "companies"}
                    </span>
                </div>
            </div>

            {selectedCompany && (
                <CompanyEditModal
                    company={selectedCompany}
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                />
            )}
        </div>
    );
};

export default Companies;
